import React, { useMemo, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "./GisCoordinates.scss";
import { EditOrganizationGisCoordinates } from "../../../services/authorization/Permissions";
import PageLayout from "../../_layout/PageLayout";
import { SettingsMenuItemsPages, SettingsMenuItemsModel } from "../SettingsMenuItems";
import {
  EditCoordinateSystem,
  DisplayCoordinateSystemDescription,
} from "../gis-coordinates/components/DataComponents";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import Form from "../../../components/form/Form";
import { GisCoordinatesStorageModel } from "../../../services/organization/GisCoordinatesStorage";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { api } from "../../../services/api/ApiService";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [gisCoordinateStorage] = useState(() => GisCoordinatesStorageModel.create({}, { api }));

  const [settingsMenuItems] = useState(() =>
    SettingsMenuItemsModel.create({ menuItemPage: SettingsMenuItemsPages.EditGisCoordinates })
  );

  const canUserEditGisCoordinates = useMemo(
    () => permissionsService.userHasPermissionTo(EditOrganizationGisCoordinates),
    []
  );

  const menuItems = {
    staticItems: settingsMenuItems.getStaticItems(),
  };

  const initialValues = useMemo(() => {
    return {
      gisCoordinatesId: gisCoordinateStorage.gisCoordinateDetails.id || null,
      coordinateSystemName: {
        id: gisCoordinateStorage.gisCoordinateDetails.id,
        name: gisCoordinateStorage.gisCoordinateDetails.name,
      },
      coordinateSystemWKID: {
        id: gisCoordinateStorage.gisCoordinateDetails.id,
        name: gisCoordinateStorage.gisCoordinateDetails.wkid,
      },
      gisCoordinateDescription: gisCoordinateStorage.gisCoordinateDetails.description,
    };
  }, [gisCoordinateStorage.gisCoordinateDetails]);

  const goToViewGisCoordinatesPage = useCallback(() => {
    history.push("/settings/gis-coordinates");
  }, [history]);

  const handleSubmit = useCallback(
    async (values: any) => {
      try {
        await gisCoordinateStorage.updateGisCoordinates(values.gisCoordinatesId);
        warningLeavePageService.setIsDirty(false);
        return goToViewGisCoordinatesPage();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.edit");
        handleErrorInSnackbar(applicationError);
      }
    },
    [gisCoordinateStorage, goToViewGisCoordinatesPage, handleErrorInSnackbar]
  );
  return (
    <div className="gis-coordinates">
      <PageLayout
        accessWithOneOfThePermissions={[EditOrganizationGisCoordinates]}
        titleKey="ViewGisCoordinates.title"
        id="edit-gis-coordinates"
        menuItems={menuItems}
        isLoading={gisCoordinateStorage.isLoading}
      >
        <Form
          formikOptions={{
            initialValues: initialValues,
            onSubmit: handleSubmit,
            validateOnBlur: false,
            validateOnChange: false,
            enableReinitialize: true,
          }}
          canSubmit={canUserEditGisCoordinates}
          onCancel={goToViewGisCoordinatesPage}
        >
          {({ values, setFieldValue }) => {
            return (
              <>
                <EditCoordinateSystem
                  name={values.coordinateSystemName}
                  WKID={values.coordinateSystemWKID}
                  setFieldValue={setFieldValue}
                  areFieldsRequired={false}
                />
                <DisplayCoordinateSystemDescription
                  gisCoordinateDescription={values.gisCoordinateDescription}
                />
              </>
            );
          }}
        </Form>
      </PageLayout>
    </div>
  );
});
